/**
 * Guest Analytics Dashboard Page
 * Personal analytics and event history for guests.
 */

import React, { useState } from 'react';
import { 
  Calendar, 
  Clock, 
  Users, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import AnalyticsCard from '../../components/Analytics/AnalyticsCard';
import Button from '../../components/UI/Button';
import Modal from '../../components/UI/Modal';
import { useGuestAnalytics } from '../../hooks/useAnalytics';
import { useAppSelector } from '../../store/hooks';
import { CompanionGuestEvent } from '../../services/guestAPI';

const GuestAnalyticsPage: React.FC = () => {
  const { theme } = useAppSelector((state) => state.ui);
  const { data, loading, error, refetch } = useGuestAnalytics();
  
  // State for companion modal
  const [selectedEvent, setSelectedEvent] = useState<{
    eventId: string;
    guestEventId: string;
    eventTitle: string;
  } | null>(null);
  const [companions, setCompanions] = useState<CompanionGuestEvent[]>([]);

  if (loading) {
    return (
      <div className={`min-h-screen w-full overflow-x-hidden ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'}`}>
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33FF00] mx-auto mb-4"></div>
              <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                Loading your analytics...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen w-full overflow-x-hidden ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'}`}>
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="text-red-500 mb-4">
                <Calendar className="w-12 h-12 mx-auto" />
              </div>
              <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                {error}
              </p>
              <Button
                onClick={refetch}
                className="mt-4"
                variant="outline"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`min-h-screen w-full overflow-x-hidden ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'}`}>
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <p className={theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                No analytics data available
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { upcoming_events, past_events, statistics } = data || {};
  
  // Ensure arrays are always defined
  const safeUpcomingEvents = Array.isArray(upcoming_events) ? upcoming_events : [];
  const safePastEvents = Array.isArray(past_events) ? past_events : [];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Function to handle companion click
  const handleCompanionsClick = (eventId: string, guestEventId: string, eventTitle: string, companionsData: Array<{id: string; name: string; phone: string; status: string}>) => {
    setSelectedEvent({ eventId, guestEventId, eventTitle });
    // Convert companions from analytics format to modal format
    const formattedCompanions: CompanionGuestEvent[] = companionsData.map(comp => ({
      id: comp.id,
      invitation_token: '',
      guest_id: '',
      event_id: eventId,
      guest_name: comp.name,
      display_name: comp.name,
      guest_phone: comp.phone || undefined,
      status: comp.status as 'pending' | 'accepted' | 'rejected' | 'ignored',
      relationship: 'other' as const,
      added_by: 'primary_guest' as const,
      is_companion: true,
      created_at: '',
      updated_at: ''
    }));
    setCompanions(formattedCompanions);
  };

  // Function to close companion modal
  const handleCloseCompanionModal = () => {
    setSelectedEvent(null);
    setCompanions([]);
  };

  return (
    <div className={`min-h-screen w-full overflow-x-hidden ${theme === 'dark' ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-gray-50 via-white to-gray-50'}`}>
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4 lg:py-8 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
          <div className="flex-shrink-0">
            <h1 className={`text-lg sm:text-xl lg:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              My Event History
            </h1>
            <p className={`text-xs sm:text-sm lg:text-base ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
              Track your event invitations and responses
            </p>
          </div>
        </div>

        {/* Statistics Cards — aligned with dashboard style */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 max-w-6xl mx-auto px-4 sm:px-6 mb-6">
          <AnalyticsCard
            title="Total Invitations"
            value={statistics?.total_invited ?? 0}
            icon={<Calendar className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Accepted Event"
            value={statistics?.accepted_events ?? 0}
            icon={<CheckCircle className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Rejected Events"
            value={statistics?.rejected_events ?? 0}
            icon={<XCircle className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Companions Brought"
            value={statistics?.companions_brought ?? 0}
            icon={<Users className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Pending Responses"
            value={statistics?.pending_events ?? 0}
            icon={<AlertCircle className="w-5 h-5" />}
          />
          
          <AnalyticsCard
            title="Avg Response Time"
            value={`${(statistics?.avg_response_time_hours ?? 0).toFixed(1)}h`}
            icon={<Clock className="w-5 h-5" />}
          />
        </div>

      {/* Upcoming Events */}
      <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
        theme === 'dark' 
          ? 'bg-black/80 border-gray-800/50' 
          : 'bg-white/90 border-gray-200/50'
      }`}>
        <div className={`absolute inset-0 ${
          theme === 'dark' 
            ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
            : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
        }`}></div>
        <div className={`absolute inset-0 opacity-5 ${
          theme === 'dark' 
            ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
            : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
        }`}></div>
        <div className="relative z-10">
          <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Upcoming Events
            </h3>
            <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
              Events you're invited to that haven't happened yet
            </p>
          </div>
          
          {safeUpcomingEvents.length === 0 ? (
            <div className={`p-6 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              No upcoming events
            </div>
          ) : (
            <div className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {safeUpcomingEvents.map((event) => (
                <div key={event.id} className={`p-6 transition-colors ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {event.title}
                    </h4>
                    <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {new Date(event.event_date).toLocaleDateString()} at {event.event_time}
                    </p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {event.location}
                    </p>
                    {event.companion_count > 0 && (
                      <button
                        onClick={() => handleCompanionsClick(event.event_id, event.id, event.title, event.companions || [])}
                        className={`text-sm mt-1 cursor-pointer hover:underline transition-colors ${
                          theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                        }`}
                      >
                        Bringing {event.companion_count} companion{event.companion_count > 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(event.status)}`}>
                      {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                    </span>
                    {getStatusIcon(event.status)}
                  </div>
                </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Past Events */}
      <div className={`relative overflow-hidden rounded-2xl shadow-2xl border backdrop-blur-sm ${
        theme === 'dark' 
          ? 'bg-black/80 border-gray-800/50' 
          : 'bg-white/90 border-gray-200/50'
      }`}>
        <div className={`absolute inset-0 ${
          theme === 'dark' 
            ? 'bg-gradient-to-br from-black via-gray-900/90 to-black' 
            : 'bg-gradient-to-br from-white via-gray-50/80 to-white'
        }`}></div>
        <div className={`absolute inset-0 opacity-5 ${
          theme === 'dark' 
            ? 'bg-gradient-to-br from-[#33FF00]/10 via-transparent to-[#33FF00]/5' 
            : 'bg-gradient-to-br from-[#33FF00]/5 via-transparent to-[#33FF00]/3'
        }`}></div>
        <div className="relative z-10">
          <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-base sm:text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Past Events
            </h3>
            <p className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
              Events that have already occurred
            </p>
          </div>
          
          {safePastEvents.length === 0 ? (
            <div className={`p-6 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              No past events
            </div>
          ) : (
            <div className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {safePastEvents.map((event) => (
                <div key={event.id} className={`p-6 transition-colors ${theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {event.title}
                    </h4>
                    <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {new Date(event.event_date).toLocaleDateString()}
                    </p>
                    {event.companion_count > 0 && (
                      <button
                        onClick={() => handleCompanionsClick(event.event_id, event.id, event.title, event.companions || [])}
                        className={`text-sm mt-1 cursor-pointer hover:underline transition-colors ${
                          theme === 'dark' ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                        }`}
                      >
                        Brought {event.companion_count} companion{event.companion_count > 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(event.status)}`}>
                      {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                    </span>
                    {getStatusIcon(event.status)}
                  </div>
                </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
        <h3 className={`text-lg font-medium mb-2 ${theme === 'dark' ? 'text-blue-100' : 'text-blue-900'}`}>
          Your Event Participation Summary
        </h3>
        <p className={theme === 'dark' ? 'text-blue-200' : 'text-blue-700'}>
          You've been invited to {statistics?.total_invited ?? 0} events total. 
          You accepted {statistics?.accepted_events ?? 0} events and rejected {statistics?.rejected_events ?? 0} events.
          {(statistics?.companions_brought ?? 0) > 0 && ` You've brought ${statistics?.companions_brought ?? 0} companions to events.`}
          {(statistics?.pending_events ?? 0) > 0 && ` You have ${statistics?.pending_events ?? 0} pending responses.`}
        </p>
      </div>
      </div>

      {/* Companions Modal */}
      <Modal
        isOpen={!!selectedEvent}
        onClose={handleCloseCompanionModal}
        title={`Companions for ${selectedEvent?.eventTitle || 'Event'}`}
        size="md"
      >
        <div className="space-y-4">
          {(companions?.length ?? 0) === 0 ? (
            <div className={`p-4 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              No companions found for this event.
            </div>
          ) : (
            <div className="space-y-3">
              {(companions || []).map((companion) => (
                <div
                  key={companion.id}
                  className={`p-4 rounded-lg border ${
                    theme === 'dark'
                      ? 'bg-gray-800/50 border-gray-700'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <div className={`p-2 rounded-full ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`}>
                      <Users className={`w-5 h-5 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {companion.display_name || companion.guest_name || 'Unknown'}
                      </h4>
                      {companion.guest_phone && (
                        <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {companion.guest_phone}
                        </p>
                      )}
                      {(companion.relationship || companion.companion_metadata?.relationship) && (
                        <span className={`inline-block mt-2 px-2 py-1 text-xs rounded ${
                          theme === 'dark'
                            ? 'bg-blue-900/30 text-blue-300'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {companion.relationship || companion.companion_metadata?.relationship || 'Companion'}
                        </span>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      companion.status === 'accepted'
                        ? theme === 'dark'
                          ? 'bg-green-900/30 text-green-300'
                          : 'bg-green-100 text-green-700'
                        : companion.status === 'rejected'
                        ? theme === 'dark'
                          ? 'bg-red-900/30 text-red-300'
                          : 'bg-red-100 text-red-700'
                        : theme === 'dark'
                        ? 'bg-yellow-900/30 text-yellow-300'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {companion.status.charAt(0).toUpperCase() + companion.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default GuestAnalyticsPage;
